import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../redux/Store';
import { logout, updateAccessToken } from '../redux/slices/authSlice';
import api from '../utils/api';
import { JWT_DURATION_MS, SESSION_WARNING_TIME_MS, TOKEN_EXPIRY_KEY } from '@/constants/auth';

export const useSessionTimeout = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { isAuthenticated } = useAppSelector((state) => state.auth);

  const [showWarning, setShowWarning] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(120);

  const warningTimerRef = useRef<NodeJS.Timeout | null>(null);
  const countdownTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isRefreshingRef = useRef(false);

  const getTokenExpiryTime = useCallback((): number | null => {
    const expiryTime = localStorage.getItem(TOKEN_EXPIRY_KEY);
    return expiryTime ? parseInt(expiryTime, 10) : null;
  }, []);

  const setTokenExpiryTime = useCallback(() => {
    const expiryTime = Date.now() + JWT_DURATION_MS;
    localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());
    return expiryTime;
  }, []);

  const clearTimers = useCallback(() => {
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
  }, []);

  // Countdown derived from actual expiry time — immune to timer throttling
  const startCountdown = useCallback((expiryTime: number) => {
    const computeRemaining = () => Math.max(0, Math.floor((expiryTime - Date.now()) / 1000));

    setSecondsRemaining(computeRemaining());

    countdownTimerRef.current = setInterval(() => {
      const remaining = computeRemaining();
      setSecondsRemaining(remaining);
      if (remaining <= 0) {
        clearTimers();
        dispatch(logout());
      }
    }, 1000);
  }, [dispatch, clearTimers]);

  // Schedule the warning timer based on wall-clock expiry
  const scheduleWarning = useCallback((expiryTime: number) => {
    clearTimers();
    const timeUntilExpiry = expiryTime - Date.now();

    if (timeUntilExpiry <= 0) {
      dispatch(logout());
      return;
    }

    if (timeUntilExpiry <= SESSION_WARNING_TIME_MS) {
      // Already in warning window
      setShowWarning(true);
      startCountdown(expiryTime);
    } else {
      const timeUntilWarning = timeUntilExpiry - SESSION_WARNING_TIME_MS;
      warningTimerRef.current = setTimeout(() => {
        setShowWarning(true);
        startCountdown(expiryTime);
      }, timeUntilWarning);
    }
  }, [clearTimers, dispatch, startCountdown]);

  const refreshToken = useCallback(async () => {
    if (isRefreshingRef.current) return;
    isRefreshingRef.current = true;
    try {
      const response = await api.post('/auth/refresh-token');
      const { accessToken } = response.data;
      dispatch(updateAccessToken(accessToken));
      const newExpiry = setTokenExpiryTime();
      setShowWarning(false);
      scheduleWarning(newExpiry);
    } catch (error) {
      console.error('[SessionTimeout] Token refresh failed:', error);
      dispatch(logout());
    } finally {
      isRefreshingRef.current = false;
    }
  }, [dispatch, setTokenExpiryTime, scheduleWarning]);

  const handleContinue = useCallback(() => { refreshToken(); }, [refreshToken]);
  const handleLogout = useCallback(() => { clearTimers(); dispatch(logout()); }, [dispatch, clearTimers]);

  // Re-evaluate state from wall-clock time — called on tab focus and on mount
  const syncFromExpiry = useCallback(() => {
    if (!isAuthenticated) return;

    const expiryTime = getTokenExpiryTime();
    if (!expiryTime) {
      const newExpiry = setTokenExpiryTime();
      scheduleWarning(newExpiry);
      return;
    }

    const timeUntilExpiry = expiryTime - Date.now();

    if (timeUntilExpiry <= 0) {
      // Expired while tab was in background
      clearTimers();
      setShowWarning(false);
      dispatch(logout());
    } else if (timeUntilExpiry <= SESSION_WARNING_TIME_MS) {
      // In warning window — update countdown to reflect true remaining time
      setShowWarning(true);
      if (!countdownTimerRef.current) {
        startCountdown(expiryTime);
      } else {
        setSecondsRemaining(Math.floor(timeUntilExpiry / 1000));
      }
    } else {
      // Still healthy — reschedule warning in case timer drifted
      scheduleWarning(expiryTime);
    }
  }, [isAuthenticated, getTokenExpiryTime, setTokenExpiryTime, scheduleWarning, clearTimers, dispatch, startCountdown]);

  // Handle tab visibility changes — fires when user returns to the tab
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        syncFromExpiry();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [syncFromExpiry]);

  // Initialize on mount / auth change
  useEffect(() => {
    if (!isAuthenticated) {
      clearTimers();
      setShowWarning(false);
      localStorage.removeItem(TOKEN_EXPIRY_KEY);
      return;
    }
    syncFromExpiry();
    return () => { clearTimers(); };
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh token proactively when navigating near expiry
  useEffect(() => {
    if (!isAuthenticated) return;
    const expiryTime = getTokenExpiryTime();
    if (!expiryTime) return;

    const timeUntilExpiry = expiryTime - Date.now();
    const REFRESH_THRESHOLD = 25 * 60 * 1000;
    if (timeUntilExpiry < REFRESH_THRESHOLD && timeUntilExpiry > 0) {
      refreshToken();
    }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh token on user activity (keyboard, click, touch, scroll). Throttled
  // so a busy user can't spam the refresh endpoint, and only fires when the
  // token is already in the "stale enough to refresh" window — matching the
  // navigation behaviour above.
  useEffect(() => {
    if (!isAuthenticated) return;

    const REFRESH_THRESHOLD = 25 * 60 * 1000;
    const MIN_REFRESH_INTERVAL = 60 * 1000; // one refresh per minute max
    let lastRefreshAt = 0;

    const handleActivity = () => {
      const expiryTime = getTokenExpiryTime();
      if (!expiryTime) return;
      const timeUntilExpiry = expiryTime - Date.now();
      if (timeUntilExpiry <= 0 || timeUntilExpiry >= REFRESH_THRESHOLD) return;
      const sinceLast = Date.now() - lastRefreshAt;
      if (sinceLast < MIN_REFRESH_INTERVAL) return;
      lastRefreshAt = Date.now();
      refreshToken();
    };

    const events: Array<keyof DocumentEventMap> = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach((e) => document.addEventListener(e, handleActivity, { passive: true }));
    return () => {
      events.forEach((e) => document.removeEventListener(e, handleActivity));
    };
  }, [isAuthenticated, getTokenExpiryTime, refreshToken]);

  return { showWarning, secondsRemaining, handleContinue, handleLogout };
};
